import { type Ref } from 'vue';
import type { useWorkspaceStore } from '../stores/workspace';
import type { EntityCategory, EntityDocument, EntityTemplate } from '../types/entities';

export function useEntityCreationActions(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  newEntity: { templateId: string; title: string; fieldValues: Record<string, unknown> };
  newEntityCollectionIds: Ref<string[]>;
  newEntityExplorerParentId: Ref<string | null | 'none'>;
  pendingNewSceneChapterId: Ref<string | null>;
  createStep: Ref<1 | 2>;
  createOpen: Ref<boolean>;
  templates: Readonly<Ref<EntityTemplate[]>>;
  visibleCategories: Readonly<Ref<EntityCategory[]>>;
  canEditWorkspace: Readonly<Ref<boolean>>;
  activeEntity: Readonly<Ref<EntityDocument | null>>;
  templateChanging: Ref<boolean>;
  templateChangeError: Ref<string>;
  flushSave: () => Promise<void>;
  hydrateDraft: () => void;
  toggleExplorerOpen: (key: string) => void;
  createCollectionFromExplorer: (key: string) => Promise<void>;
}) {
  const { store, newEntity, newEntityCollectionIds, newEntityExplorerParentId, pendingNewSceneChapterId, createStep, createOpen, templates, visibleCategories, canEditWorkspace, activeEntity, templateChanging, templateChangeError, flushSave, hydrateDraft, toggleExplorerOpen, createCollectionFromExplorer } = options;
function chooseCategory(categoryKey: string) {
  const template = templates.value.find((item) => item.category === categoryKey);
  if (!template) return;
  if (newEntity.templateId !== template.id) {
    newEntity.fieldValues = {};
    newEntityCollectionIds.value = [];
  }
  newEntity.templateId = template.id;
}
function chooseTemplate(templateId: string) {
  if (newEntity.templateId !== templateId) newEntity.fieldValues = {};
  newEntity.templateId = templateId;
}
async function changeActiveEntityTemplate(templateId: string) {
  if (!activeEntity.value || templateId === activeEntity.value.templateId || templateChanging.value) return;
  templateChangeError.value = '';
  templateChanging.value = true;
  try {
    await flushSave();
    await store.changeEntityTemplate(activeEntity.value.id, templateId);
    hydrateDraft();
  } catch (error) {
    templateChangeError.value = error instanceof Error ? error.message : 'Template konnte nicht geändert werden.';
  } finally {
    templateChanging.value = false;
  }
}
function selectExplorerTemplate(templateId: string) {
  newEntity.templateId = templateId;
  try { localStorage.setItem('chronis.explorer.create-template', templateId); } catch { /* optional */ }
}
function openCreateFromExplorer() {
  if (!canEditWorkspace.value) return;
  try { const saved = localStorage.getItem('chronis.explorer.create-template'); if (saved && templates.value.some((item) => item.id === saved)) newEntity.templateId = saved; } catch { /* optional */ }
  newEntity.title = '';
  newEntity.fieldValues = {};
  newEntityCollectionIds.value = [];
  newEntityExplorerParentId.value = 'none';
  pendingNewSceneChapterId.value = null;
  createStep.value = 1;
  createOpen.value = true;
}
function handleExplorerTreeClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (target.closest('button, input, select, a')) return;
  const templateHeader = target.closest<HTMLElement>('.template-subcategory-header');
  if (templateHeader) {
    const templateName = templateHeader.querySelector('.template-subcategory-label')?.textContent?.trim();
    const template = templates.value.find((item) => item.name === templateName);
    if (template) { toggleExplorerOpen(`template-${template.id}`); selectExplorerTemplate(template.id); }
    return;
  }
  const categoryHeader = target.closest<HTMLElement>('.category-view > p');
  if (categoryHeader) {
    const name = categoryHeader.querySelector('span')?.textContent?.trim();
    const category = visibleCategories.value.find((item) => item.name === name);
    if (category) {
      if (event.detail > 1) void createCollectionFromExplorer(category.key);
      else toggleExplorerOpen(`category-${category.id}`);
    }
  }
}
  return { chooseCategory, chooseTemplate, changeActiveEntityTemplate, selectExplorerTemplate, openCreateFromExplorer, handleExplorerTreeClick };
}
